<template>
  <div class="crv-app-account-menu-content rounded-lg">
    <div class="crv-app-account-menu-content_user">
      <div class="crv-app-account-menu-content_user-label">
        {{ label }}
      </div>
      <div class="crv-app-account-menu-content_user-name">
        {{ name }}
      </div>
      <div class="divider" />
    </div>
    <slot name="customer-name">
      <div v-if="allowSwitchCustomer">
        <v-tooltip
          activator="parent"
          location="bottom"
        >
          {{ tooltipCustomerSwitcher }}
        </v-tooltip>
        <div
          v-if="hasCustomerName"
          class="crv-app-account-menu-content_customer"
          @click="handleClickCustomer"
        >
          <v-icon
            :icon="swapHorizontal"
            class="mr-2"
          />
          <div class="crv-app-account-menu-content_customer-content">
            {{ computedCustomerName }}
          </div>
        </div>
        <div
          v-else
          class="crv-app-account-menu-content_no-customer"
          @click="handleClickCustomer"
        >
          <v-icon
            :icon="swapHorizontal"
            class="mr-2"
          />
          <div class="crv-app-account-menu-content_no-customer-content">
            {{ selectCustomerText }}
          </div>
        </div>
        <div class="divider" />
      </div>
    </slot>
    <slot
      v-if="hasLocations"
      name="locations"
    >
      <div class="crv-app-account-menu-content_locations">
        <div class="crv-app-account-menu-content_locations-label">
          {{ locationsLabel }}
        </div>

        <div
          v-if="hasMultipleLocations"
          class="crv-app-account-menu-content_locations-location"
          :class="{ 'is-selected': selectAllSelected}"
          @click="selectAllLocations"
        >
          <v-icon
            :icon="selectAllSelected ? mdiMapMarkerCheck : mdiMapMarker"
            :class="{ 'is-selected': selectAllSelected }"
            class="mr-2"
          />
          {{ selectAllLocationsLabel }}
        </div>

        <div
          v-for="location in locations"
          :key="location.id"
          class="crv-app-account-menu-content_locations-location"
          :class="{ 'is-selected': !selectAllSelected && selectedLocations.includes(location.id) }"
          @click="hasMultipleLocations ? handleClickLocation(location.id) : null"
        >
          <v-icon
            :icon="!selectAllSelected && selectedLocations.includes(location.id) ? mdiMapMarkerCheck : mdiMapMarker"
            :class="{ 'is-selected': !selectAllSelected && selectedLocations.includes(location.id) }"
            class="mr-2"
          />
          <div class="crv-app-account-menu-content_locations-text">
            {{ location.label }}
          </div>
        </div>
      </div>
      <div class="divider" />
    </slot>
    <div
      class="crv-app-account-menu-content_logout"
      @click="handleLogout"
    >
      <v-icon
        :icon="logout"
        class="mr-2"
      />
      {{ logoutLabel }}
    </div> 
  </div>
</template>


<script>
import { mdiAccount, mdiHelpCircle, mdiLogout,mdiOpenInNew, mdiSwapHorizontal, mdiMapMarker, mdiMapMarkerCheck } from '@mdi/js'
import storageHelper from '@v3c/security/storageHelper'


export default {
    name: 'CrvAppAccountMenuContent',

    props: {
        name: {
            type: String,
            default: undefined,
        },

        label: {
            type: String,
            default: '$t("crvAppAccountMenuContent.loggedInAs")',
        },

        allowSwitchCustomer: {
            type: Boolean,
            default: false,
        },

        customerName: {
            type: String,
            default: undefined,
        },

        locations: {
            type: Array,
            default: () => [],
        },

        locationsLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.locations")',
        },

        selectAllLocationsLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.selectAllLocations")',
        },

        logoutLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.logout")',
        },

        marketCountryCode: {
            type: String,
            default: 'NL',
        },

        selectCustomerText: {
            type: String,
            default: '$t("crvAppAccountMenuContent.selectCustomer")',
        },

        selectCustomerRoute: {
            type: [Object, String],
            default: null,
        },

        tooltipCustomerSwitcher: {
            type: String,
            default: '$t("crvAppAccountMenuContent.switchCustomer")',
        },

        beforeNavigateToCustomerSwitcher: {
            type: Function,
            default: async () => true,
        },
    },

    emits: [
        'logout',
        'click-location',
        'close',
    ],

    data() {
        return {
            events: {
                logout: {
                    emit: (event) => this.$emit('logout', event),
                    description: 'logout event was triggered',
                    params: 'click event',
                },
                close: {
                    emit: () => this.$emit('close'),
                    description: 'close event was triggered',
                },
                clickLocation: {
                    emit: (location) => this.$emit('click-location', location),
                    description: 'location was clicked',
                },
            },
            selectedLocations: [],
            selectAllSelected: false,
            swapHorizontal: mdiSwapHorizontal,
            account: mdiAccount,
            openInNew: mdiOpenInNew,
            helpCircle: mdiHelpCircle,
            logout: mdiLogout,
            mdiMapMarker,
            mdiMapMarkerCheck,
        }
    },
    

    computed: {
        hasLocations() {
            return this.locations && this.locations.length > 0
        },

        hasMultipleLocations() {
            return this.locations && this.locations.length > 1
        },

        hasCustomerName() {
            return !!this.customerName || this.hasAuthorizedCustomerName
        },

        hasAuthorizedCustomerName() {
            return storageHelper.loadAuthorizedCustomers()?.includes(storageHelper.loadCustomerNumber())
        },

        computedCustomerName() {
            return this.customerName || storageHelper.loadCustomerName()
        },
    },

    watch: {
      locations: {
        handler() {
          this.syncFromProps()
        },
        deep: true,
      },
    },

    mounted() {
      this.syncFromProps()
    },

    methods: {
        syncFromProps() {
          if (!this.locations?.length) {
            this.selectedLocations = []
            this.selectAllSelected = false
            return
          }

          const explicitlySelectedIds = this.locations
          .filter(l => l.isSelected)
          .map(l => l.id)

          if (explicitlySelectedIds.length === 0 && this.locations.length > 1) {
            this.selectAllSelected = true
            this.selectedLocations = [] // empty means "all"
            return
          }

          this.selectAllSelected = false
          if (explicitlySelectedIds.length) {
            this.selectedLocations = explicitlySelectedIds
          } else {
            // single-location (or fallback)
            this.selectedLocations = this.locations.length ? [this.locations[0].id] : []
          }
        },


        handleLogout(event) {
            this.events.logout.emit(event)
            this.events.close.emit()
        },

        handleClickLocation(locationId) {
            // selecting a single specific location
            this.selectAllSelected = false
            this.selectedLocations = [locationId]

            // emit a *single* id (parent expects either id or array)
            this.events.clickLocation.emit(locationId)
        },

        async handleClickCustomer() {
            if (this.allowSwitchCustomer) {
                const allowedToNavigate = await this.beforeNavigateToCustomerSwitcher()
                if (allowedToNavigate) {
                    const fromRoute = this.$route.fullPath
                    sessionStorage.setItem('returnAfterCustomerSelect', fromRoute)
                    
                    const route = this.selectCustomerRoute || { name: 'select-customer' }
                    this.$router.push(route)
                }
            }
        },

        selectAllLocations() {
            this.selectAllSelected = true
            this.selectedLocations = [] // empty is allSelected

            // emit array of all ids to parent
            const allIds = this.locations.map(location => location.id)
            this.events.clickLocation.emit(allIds)
        },
    },
}
</script>

<style lang="scss" scoped>
@use '@v3c/assets/styles/variables.scss' as *;

$menu-width: $horizontal-spacing-single * 30;
$menu-border-radius: 10px;
$text-color: $base-gray-90;
$menu-item-color: $base-gray-80;
$divider-color: $base-gray-40;
$border-color: $base-gray-40;
$shadow-color: rgba(0, 0, 0, 0.24);

.crv-app-account-menu-content {
    display: flex;
    flex-direction: column;
    width: $menu-width;
    padding: $vertical-spacing-double $horizontal-spacing-double;
    background-color: $base-white;
    box-shadow: 0 2px 4px 0 rgba(0,0,0,0.30);
    border-radius: $menu-border-radius;

    &_user,
    &_customer,
    &_no-customer,
    &_locations,
    &_logout {
        width: 100%;
        min-height: $element-height-4;
        color: $text-color;
    }

    &_user {
        display: flex;
        flex-direction: column;

        &-label {
            color: $base-gray-90;
            font-size: $font-size-xs;
            font-style: $font-style-emphasis;
        }

        &-name {
            color: $base-gray-90;
            font-weight: $font-weight-strong;
            padding-bottom: $vertical-spacing-double;
        }
    }

    &_customer,
    &_no-customer {
        display: flex;
        align-items: center;
        margin: $vertical-spacing-single 0;
        padding: $vertical-spacing-single $horizontal-spacing-single;
        cursor: pointer;
        border-radius: $menu-border-radius;

        &:hover {
            background-color: $base-gray-5;
        }

        .crv-svg-icon {
            fill: $base-blue-80;
            margin-right: $horizontal-spacing-single;
            min-width: fit-content;
        }
    }

    &_no-customer {
        color: $base-gray-80;
    }

    .divider {
        border-bottom: $border-single solid $divider-color;
    }

    &_locations {
        padding-bottom: $vertical-spacing-single;

        &-label {
            color: $base-gray-90;
            font-weight: $font-weight-strong;
            margin: $vertical-spacing-single 0;
        }

        &-location {
            display: flex;
            padding: $vertical-spacing-single $horizontal-spacing-single;
            cursor: pointer;
            border-radius: $menu-border-radius;

            &.is-selected {
                background-color: $base-blue-5;

                &:hover {
                    background-color: $base-blue-5;
                }
            }

            &:hover {
                background-color: $base-gray-5;
            }

            .crv-svg-icon {
                margin-right: $horizontal-spacing-single;
                fill: $base-blue-80;
                visibility: hidden;

                &.is-selected {
                    visibility: visible;
                }
            }
        }
    }

    &_logout {
        display: flex;
        align-items: center;
        margin-top: $vertical-spacing-single;
        padding: $vertical-spacing-single $horizontal-spacing-single;
        cursor: pointer;
        border-radius: $menu-border-radius;

        &:hover {
            background-color: $base-gray-5;
        }

        .crv-svg-icon {
            margin-top: $vertical-spacing-half;
            margin-right: $horizontal-spacing-single;
            fill: $menu-item-color;
        }
    }

    .link-icon {
        fill: $base-white;
        margin-left: auto;
        margin-right: 0;
    }
}

</style>